import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { delay, map, catchError, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { PermissionService } from './permission.service';
import { UsersService } from './users.service';

export interface User {
  userId: number;
  username: string;
  email: string;
  isActive: boolean;
  profileIds: number[];
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject: BehaviorSubject<User | null>;
  public currentUser: Observable<User | null>;
  private baseUrl = `${environment.microservices.auth}/auth`;
  private readonly staffIdStorageKey = 'currentStaffId';

  constructor(
    private http: HttpClient,
    private permissionService: PermissionService,
    private usersService: UsersService
  ) {
    this.currentUserSubject = new BehaviorSubject<User | null>(
      JSON.parse(localStorage.getItem('currentUser') || 'null')
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json'
    });
  }

  private buildUrl(endpoint: string): string {
    return `${this.baseUrl}${endpoint}`;
  }

  public get currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }

  login(email: string, password: string): Observable<User | null> {
    const body = { email, password };
    
    return this.http.post<any>(this.buildUrl('/login'), body, { headers: this.getHeaders() }).pipe(
      tap(response => {
        if (response && response.success && response.user) {
          localStorage.setItem('currentUser', JSON.stringify(response.user));
          localStorage.setItem('authToken', response.token);
          // Carregar permissões e grupos do JWT após login
          this.permissionService.loadPermissionsFromToken();
          this.permissionService.loadGroupsFromToken();
          // Salvar o último email e username logado
          localStorage.setItem('lastLoggedEmail', email);
          if (response.user.username) {
            localStorage.setItem('lastLoggedUsername', response.user.username);
          }
          // Salvar o userId para recuperar avatar depois
          if (response.user.userId) {
            localStorage.setItem('lastLoggedUserId', response.user.userId.toString());
          }

          // Tentar capturar staffId cedo (response.user / JWT) e cachear
          const staffIdFromLogin = this.parseStaffId(
            response?.user?.staffId ??
              response?.user?.StaffId ??
              response?.user?.employeeId ??
              response?.user?.EmployeeId ??
              response?.user?.colaboradorId ??
              response?.user?.ColaboradorId
          );
          if (staffIdFromLogin) {
            this.setStaffIdCache(staffIdFromLogin);
          } else {
            const staffIdFromJwt = this.getStaffIdFromToken();
            if (staffIdFromJwt) {
              this.setStaffIdCache(staffIdFromJwt);
            } else {
              // Fallback: buscar do serviço de usuários (não bloquear o login)
              const rawUserId = response?.user?.userId ?? this.getUserIdFromToken();
              const userId = Number(rawUserId);
              if (Number.isFinite(userId) && userId > 0) {
                this.usersService
                  .getUserById(userId)
                  .pipe(
                    map((profile) => this.extractStaffIdFromProfile(profile)),
                    catchError(() => of(null))
                  )
                  .subscribe((staffId) => {
                    if (staffId) {
                      this.setStaffIdCache(staffId);
                      this.persistStaffIdOnStoredUser(staffId);
                    }
                  });
              }
            }
          }

          this.currentUserSubject.next(response.user);
        }
      }),
      map(response => response?.user || null),
      catchError(error => {
        console.error('Erro ao fazer login:', error);
        return of(null);
      })
    );
  }

  register(username: string, email: string, password: string): Observable<User | null> {
    const body = { username, email, password };
    
    return this.http.post<any>(this.buildUrl('/register'), body, { headers: this.getHeaders() }).pipe(
      tap(response => {
        if (response && response.user) {
          localStorage.setItem('currentUser', JSON.stringify(response.user));
          localStorage.setItem('authToken', response.token);
          this.currentUserSubject.next(response.user);
        }
      }),
      map(response => response?.user || null),
      catchError(error => {
        console.error('Erro ao registrar:', error);
        return of(null);
      })
    );
  }

  logout(): void {
    // Não remover o lastLoggedEmail para manter após logout
    localStorage.removeItem('currentUser');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userGroups');
    localStorage.removeItem(this.staffIdStorageKey);
    this.permissionService.clearUserGroups();
    this.permissionService.clearPermissions();
    this.currentUserSubject.next(null);
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token || !this.currentUserValue) {
      return false;
    }
    return !this.isTokenExpired(token);
  }

  getToken(): string | null {
    return localStorage.getItem('authToken');
  }

  private decodeTokenPayload(token: string): any | null {
    try {
      const payloadBase64 = token.split('.')[1];
      if (!payloadBase64) {
        return null;
      }

      const normalizedBase64 = payloadBase64
        .replace(/-/g, '+')
        .replace(/_/g, '/');

      const paddedBase64 = normalizedBase64.padEnd(Math.ceil(normalizedBase64.length / 4) * 4, '=');
      return JSON.parse(atob(paddedBase64));
    } catch (error) {
      console.error('Erro ao decodificar token JWT:', error);
      return null;
    }
  }

  private parseDepartmentId(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private parseCostCenterId(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private getCostCenterIdFromStoredUser(): number | null {
    const currentUser = this.currentUserValue as any;
    const storedUser = currentUser ?? JSON.parse(localStorage.getItem('currentUser') || 'null');

    return this.parseCostCenterId(
      storedUser?.costCenterId ??
        storedUser?.CostCenterId ??
        storedUser?.cost_center_id ??
        storedUser?.centroCustoId ??
        storedUser?.CentroCustoId
    );
  }

  /**
   * Centro de custo do usuário logado (JWT + objeto salvo no login).
   * Usado p.ex. na listagem «Solicitação de Compras» por centro de custo.
   */
  getCostCenterIdFromToken(): number | null {
    const token = this.getToken();
    if (!token) {
      return this.getCostCenterIdFromStoredUser();
    }

    try {
      const payload = this.decodeTokenPayload(token);
      if (!payload) {
        return this.getCostCenterIdFromStoredUser();
      }

      const ccId =
        payload.costCenterId ??
        payload.CostCenterId ??
        payload.cost_center_id ??
        payload.centroCustoId ??
        payload.CentroCustoId ??
        null;

      return this.parseCostCenterId(ccId) ?? this.getCostCenterIdFromStoredUser();
    } catch (error) {
      console.error('Erro ao extrair costCenterId do token:', error);
      return this.getCostCenterIdFromStoredUser();
    }
  }

  private getDepartmentIdFromStoredUser(): number | null {
    const currentUser = this.currentUserValue as any;
    const storedUser = currentUser ?? JSON.parse(localStorage.getItem('currentUser') || 'null');

    return this.parseDepartmentId(
      storedUser?.departmentId
      ?? storedUser?.DepartmentId
      ?? storedUser?.unidadeId
      ?? storedUser?.UnidadeId
      ?? storedUser?.branchId
      ?? storedUser?.BranchId
    );
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = this.decodeTokenPayload(token);
      const exp = payload.exp;
      if (!exp) return true;
      const now = Math.floor(Date.now() / 1000);
      return now >= exp;
    } catch (error) {
      console.error('Erro ao verificar expiração do token:', error);
      return true;
    }
  }

  getUserNameFromToken(): string {
    const token = this.getToken();
    if (!token) return '';
    
    try {
      const payload = this.decodeTokenPayload(token);
      if (!payload) return '';
      // Prioriza unique_name para auditoria e integrações com backend.
      return payload.unique_name || payload.name || payload.username || payload.sub || '';
    } catch (error) {
      console.error('Erro ao extrair nome do token:', error);
      return '';
    }
  }

  getUniqueNameFromToken(): string {
    const token = this.getToken();
    if (!token) return '';

    try {
      const payload = this.decodeTokenPayload(token);
      if (!payload) return '';
      return payload.unique_name || '';
    } catch (error) {
      console.error('Erro ao extrair unique_name do token:', error);
      return '';
    }
  }

  getUserIdFromToken(): string | null {
    const token = this.getToken();
    if (!token) return null;
    
    try {
      const payload = this.decodeTokenPayload(token);
      if (!payload) return null;
      // Tenta diferentes campos comuns para userId
      const userId = payload.userId || payload.sub || payload.nameid || payload.id;
      return userId ? String(userId) : null;
    } catch (error) {
      console.error('Erro ao extrair userId do token:', error);
      return null;
    }
  }

  private parseStaffId(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  /**
   * Retorna o staffId do usuário logado (preferencialmente via JWT).
   * Fallback: tenta ler do currentUser salvo no localStorage.
   */
  getStaffIdFromToken(): number | null {
    const token = this.getToken();

    const fromStoredUser = () => {
      const currentUser = this.currentUserValue as any;
      const storedUser = currentUser ?? JSON.parse(localStorage.getItem('currentUser') || 'null');
      return this.parseStaffId(
        storedUser?.staffId ??
          storedUser?.StaffId ??
          storedUser?.employeeId ??
          storedUser?.EmployeeId ??
          storedUser?.colaboradorId ??
          storedUser?.ColaboradorId
      );
    };

    if (!token) {
      return fromStoredUser();
    }

    try {
      const payload = this.decodeTokenPayload(token);
      if (!payload) {
        return fromStoredUser();
      }

      const staffId =
        payload.staffId ??
        payload.StaffId ??
        payload.staff_id ??
        payload.employeeId ??
        payload.EmployeeId ??
        payload.colaboradorId ??
        payload.ColaboradorId ??
        payload.currentStaffId ??
        payload.CurrentStaffId ??
        payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/staffid'] ??
        payload['staffid'] ??
        null;

      const fromClaims =
        this.parseStaffId(staffId) ??
        this.parseStaffId(payload.nameid) ??
        this.parseStaffId(
          payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier']
        );

      return fromClaims ?? fromStoredUser();
    } catch (error) {
      console.error('Erro ao extrair staffId do token:', error);
      return fromStoredUser();
    }
  }

  /**
   * Retorna o staffId do usuário logado com fallback via API de usuários.
   * - 1) tenta JWT/localStorage (síncrono)
   * - 2) tenta cache local (`currentStaffId`)
   * - 3) busca o profile do usuário pelo userId (como no "Meu Perfil") e extrai StaffId
   */
  getStaffId(): Observable<number | null> {
    const fromSyncSources = this.getStaffIdFromToken();
    if (fromSyncSources) {
      this.setStaffIdCache(fromSyncSources);
      return of(fromSyncSources);
    }

    const cached = this.getStaffIdFromCache();
    if (cached) {
      return of(cached);
    }

    const rawUserId =
      this.getUserIdFromToken() ??
      (this.currentUserValue as any)?.userId ??
      localStorage.getItem('lastLoggedUserId');

    const userId = Number(rawUserId);
    if (!Number.isFinite(userId) || userId <= 0) {
      return of(null);
    }

    return this.usersService.getUserById(userId).pipe(
      map((profile) => this.extractStaffIdFromProfile(profile)),
      tap((staffId) => {
        if (staffId) {
          this.setStaffIdCache(staffId);
          this.persistStaffIdOnStoredUser(staffId);
        }
      }),
      catchError(() => of(null))
    );
  }

  private getStaffIdFromCache(): number | null {
    return this.parseStaffId(localStorage.getItem(this.staffIdStorageKey));
  }

  private setStaffIdCache(staffId: number): void {
    localStorage.setItem(this.staffIdStorageKey, String(staffId));
  }

  private extractStaffIdFromProfile(profile: any): number | null {
    return this.parseStaffId(
      profile?.StaffId ??
        profile?.staffId ??
        profile?.StaffID ??
        profile?.staffID ??
        profile?.employeeId ??
        profile?.EmployeeId ??
        profile?.colaboradorId ??
        profile?.ColaboradorId ??
        profile?.Staff?.Id ??
        profile?.Staff?.id
    );
  }

  private persistStaffIdOnStoredUser(staffId: number): void {
    try {
      const storedUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
      if (!storedUser || typeof storedUser !== 'object') return;
      if (storedUser.staffId === staffId || storedUser.StaffId === staffId) return;

      storedUser.staffId = staffId;
      localStorage.setItem('currentUser', JSON.stringify(storedUser));
    } catch {
      // no-op
    }
  }

  getCostCenterFromToken(): string | null {
    const token = this.getToken();
    if (!token) return null;
    try {
      const payload = this.decodeTokenPayload(token);
      return payload?.CostCenter || payload?.costCenter || null;
    } catch {
      return null;
    }
  }

  getManagerFromToken(): string | null {
    const token = this.getToken();
    if (!token) return null;
    try {
      const payload = this.decodeTokenPayload(token);
      return payload?.Manager || payload?.manager || null;
    } catch {
      return null;
    }
  }

  getDepartmentIdFromToken(): number | null {
    const token = this.getToken();
    if (!token) return this.getDepartmentIdFromStoredUser();

    try {
      const payload = this.decodeTokenPayload(token);
      if (!payload) {
        return this.getDepartmentIdFromStoredUser();
      }

      const departmentId = payload.departmentId ?? payload.DepartmentId ?? payload.department_id
        ?? payload.unidadeId ?? payload.UnidadeId
        ?? payload.branchId ?? payload.BranchId
        ?? payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/groupsid']
        ?? null;

      return this.parseDepartmentId(departmentId) ?? this.getDepartmentIdFromStoredUser();
    } catch (error) {
      console.error('Erro ao extrair departmentId do token:', error);
      return this.getDepartmentIdFromStoredUser();
    }
  }

  // Método para validar código de reset de senha e alterar a senha
  passwordCodeValidate(email: string, code: string, newPassword: string): Observable<{ success: boolean; message?: string }> {
    const body = { email, code, newPassword };
    
    return this.http.post<any>(this.buildUrl('/validate-password-code'), body, { headers: this.getHeaders() }).pipe(
      map(response => ({ success: !!response?.success, message: response?.message })),
      catchError(error => {
        console.error('Erro ao validar código:', error);
        const message = error?.error?.message || 'Erro ao validar código.';
        return of({ success: false, message });
      })
    );
  }

  // Alias para compatibilidade com componentes antigos
  verifyResetCode(email: string, code: string, newPassword: string): Observable<{ success: boolean; message?: string }> {
    return this.passwordCodeValidate(email, code, newPassword);
  }

  // Método para solicitar reset de senha (envia código por email)
  requestPasswordReset(email: string): Observable<{ success: boolean; message?: string }> {
    const body = { email };
    
    return this.http.post<any>(this.buildUrl('/reset-password'), body, { headers: this.getHeaders() }).pipe(
      map(response => ({ success: response?.success || false, message: response?.message })),
      catchError(error => {
        console.error('Erro ao solicitar reset de senha:', error);
        return of({ success: false, message: 'Erro ao enviar email de reset de senha' });
      })
    );
  }

  // Método para obter o último email logado
  getLastLoggedEmail(): string | null {
    return localStorage.getItem('lastLoggedEmail');
  }

  // Método para obter o último username logado
  getLastLoggedUsername(): string | null {
    return localStorage.getItem('lastLoggedUsername');
  }

  // Método para limpar o último email e username salvos
  clearLastLoggedEmail(): void {
    localStorage.removeItem('lastLoggedEmail');
    localStorage.removeItem('lastLoggedUsername');
  }

  /**
   * Extrai os IDs de ConfigOrders permitidos do token JWT.
   * Prioriza ConfigOrdersIds (CSV), depois ProfileConfigOrders (JSON/array).
   * Retorna array vazio se não houver ConfigOrders permitidas.
   */
  getAllowedConfigOrderIdsFromToken(): number[] {
    const token = this.getToken();
    if (!token) return [];

    try {
      const payload = this.decodeTokenPayload(token);
      if (!payload) return [];

      // Prioridade 1: ConfigOrdersIds (formato CSV esperado, ex: "1,2,3")
      const configOrdersIds = payload.ConfigOrdersIds ?? payload.configOrdersIds ?? payload.ConfigOrderIds;
      if (configOrdersIds) {
        const normalized = this.normalizeConfigOrderIds(configOrdersIds);
        if (normalized.length > 0) return normalized;
      }

      // Prioridade 2: ProfileConfigOrders (formato JSON array ou array direto)
      const profileConfigOrders = payload.ProfileConfigOrders ?? payload.profileConfigOrders;
      if (profileConfigOrders) {
        const normalized = this.normalizeConfigOrderIds(profileConfigOrders);
        if (normalized.length > 0) return normalized;
      }

      return [];
    } catch (error) {
      console.error('Erro ao extrair ConfigOrders do token:', error);
      return [];
    }
  }

  /**
   * Verifica se o usuário tem pelo menos um tipo de OS permitido.
   */
  hasAllowedConfigOrders(): boolean {
    return this.getAllowedConfigOrderIdsFromToken().length > 0;
  }

  /**
   * Normaliza diferentes formatos de ConfigOrders em array de IDs numéricos.
   * Aceita: CSV ("1,2,3"), JSON array ("[1,2,3]"), array direto, objetos com id/Id/ConfigOrderId.
   */
  private normalizeConfigOrderIds(value: any): number[] {
    if (!value) return [];

    // Se já é array
    if (Array.isArray(value)) {
      return value
        .map(item => {
          if (typeof item === 'number') return item;
          if (typeof item === 'string') return Number(item);
          // Se for objeto, tenta extrair id
          return Number(item?.id ?? item?.Id ?? item?.ConfigOrderId ?? item?.configOrderId ?? item);
        })
        .filter(n => Number.isFinite(n) && n > 0);
    }

    // Se é string
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];

      // Tenta parsear como JSON array
      if (trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            return this.normalizeConfigOrderIds(parsed);
          }
        } catch {
          // Não é JSON válido, continua para CSV
        }
      }

      // Assume CSV
      return trimmed
        .split(',')
        .map(part => Number(part.trim()))
        .filter(n => Number.isFinite(n) && n > 0);
    }

    return [];
  }

}
